<template>
  <v-dialog
    :value="value"
    persistent
    :fullscreen="$isMobile()"
    :max-width="$isMobile() ? undefined : 560"
    content-class="mp-brick-dialog"
    @input="$emit('input', $event)"
  >
    <v-card class="mp-brick-card">
      <v-card-title class="mp-brick-title">
        <span>Pagar con Mercado Pago</span>
        <v-spacer />
        <v-btn icon :disabled="processing" @click="onCancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="pedidoId" class="pb-0">
        Pedido N° {{ pedidoId }} — Total ${{ Number(amount).toFixed(2) }}
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text class="mb-3">
          {{ errorMessage }}
        </v-alert>
        <v-alert v-if="infoMessage" type="info" dense text class="mb-3">
          {{ infoMessage }}
        </v-alert>
        <div v-show="processing" class="mp-brick-loading text-center py-6">
          <v-progress-circular indeterminate color="primary" />
          <p class="mt-3 mb-0">Procesando pago…</p>
        </div>
        <div id="paymentBrick_container" ref="brickContainer"></div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'

const SDK_SRC = 'https://sdk.mercadopago.com/js/v2'
const SDK_SCRIPT_ID = 'mercadopago-sdk-v2'

function loadMercadoPagoSdk () {
  return new Promise((resolve, reject) => {
    if (window.MercadoPago) {
      resolve(window.MercadoPago)
      return
    }
    let script = document.getElementById(SDK_SCRIPT_ID)
    if (!script) {
      script = document.createElement('script')
      script.id = SDK_SCRIPT_ID
      script.src = SDK_SRC
      script.async = true
      document.head.appendChild(script)
    }
    script.addEventListener('load', () => resolve(window.MercadoPago))
    script.addEventListener('error', () => reject(new Error('No se pudo cargar el SDK de Mercado Pago')))
  })
}

function publicBaseUrl () {
  const configured = process.env.VUE_APP_PUBLIC_BASE_URL
  if (configured && configured.trim() !== '') return configured.trim()
  return `${window.location.protocol}//${window.location.host}`
}

export default {
  name: 'MercadoPagoBrick',
  props: {
    value: { type: Boolean, default: false },
    publicKey: { type: String, required: true },
    amount: { type: [Number, String], required: true },
    pedidoId: { type: [Number, String], required: true },
    preferenceId: { type: String, required: true },
    payerEmail: { type: String, default: '' },
    description: { type: String, default: 'Pedido por Clickaway' }
  },
  data () {
    return {
      processing: false,
      errorMessage: null,
      infoMessage: null,
      bricksBuilder: null,
      paymentBrickController: null
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (open) {
        if (open) {
          this.$nextTick(() => this.mountBrick())
        } else {
          this.unmountBrick()
        }
      }
    }
  },
  beforeDestroy () {
    this.unmountBrick()
  },
  methods: {
    ...mapActions('Carrito', ['saveMercadoPagoPayment']),
    onCancel () {
      if (this.processing) return
      this.$emit('cancel')
      this.$emit('input', false)
    },
    async unmountBrick () {
      try {
        if (this.paymentBrickController && typeof this.paymentBrickController.unmount === 'function') {
          this.paymentBrickController.unmount()
        }
      } catch (e) { /* ignore */ }
      this.paymentBrickController = null
      this.bricksBuilder = null
      const el = document.getElementById('paymentBrick_container')
      if (el) el.innerHTML = ''
    },
    async mountBrick () {
      this.errorMessage = null
      this.infoMessage = null
      this.processing = false
      await this.unmountBrick()

      if (!this.publicKey) {
        this.errorMessage = 'No hay Public Key de Mercado Pago configurada para esta tienda.'
        return
      }

      try {
        await loadMercadoPagoSdk()
        const mp = new window.MercadoPago(this.publicKey, { locale: 'es-AR' })
        this.bricksBuilder = mp.bricks()

        const amount = Number(this.amount)
        const settings = {
          initialization: {
            amount,
            preferenceId: this.preferenceId,
            payer: this.payerEmail ? { email: this.payerEmail } : undefined
          },
          customization: {
            paymentMethods: {
              creditCard: 'all',
              debitCard: 'all',
              mercadoPago: 'all'
            }
          },
          callbacks: {
            onReady: () => {},
            onError: (error) => {
              console.error('[MP Brick] error', error)
              this.errorMessage = (error && error.message) || 'Error en el formulario de pago'
            },
            onSubmit: ({ formData }) => {
              return this.processPayment(formData)
            }
          }
        }

        this.paymentBrickController = await this.bricksBuilder.create(
          'payment',
          'paymentBrick_container',
          settings
        )
      } catch (err) {
        console.error('[MP Brick] mount failed', err)
        this.errorMessage = (err && err.message) || 'No se pudo inicializar el pago'
      }
    },
    async processPayment (formData) {
      this.processing = true
      this.errorMessage = null
      this.infoMessage = null

      const code = this.$clientCode()
      const payload = {
        ...formData,
        transaction_amount: formData.transaction_amount != null
          ? Number(formData.transaction_amount)
          : Number(this.amount),
        description: this.description,
        external_reference: String(this.pedidoId),
        ClientCode: code,
        BaseUrl: publicBaseUrl()
      }

      if (payload.issuer_id != null) {
        payload.issuer_id = String(payload.issuer_id)
      }

      try {
        const result = await this.saveMercadoPagoPayment(payload)
        if (result.pagoaprobado) {
          this.$emit('paid', {
            pedidoId: this.pedidoId,
            paymentId: result.pagoid,
            status: 'approved'
          })
          this.$emit('input', false)
          return
        }

        if (result.status === 'pending' || result.status === 'in_process') {
          this.infoMessage = 'El pago quedó pendiente. Te avisaremos cuando se confirme.'
          this.$emit('pending', {
            pedidoId: this.pedidoId,
            paymentId: result.pagoid,
            status: result.status,
            statusDetail: result.message
          })
          return
        }

        this.errorMessage = result.message || 'El pago fue rechazado. Probá con otro medio.'
        throw new Error(this.errorMessage)
      } catch (err) {
        const msg = (err && err.message) || 'No se pudo procesar el pago'
        this.errorMessage = msg
        throw err
      } finally {
        this.processing = false
      }
    }
  }
}
</script>

<style scoped>
.mp-brick-card {
  min-height: 280px;
}
.mp-brick-title {
  word-break: break-word;
}
.mp-brick-loading {
  color: rgba(0, 0, 0, 0.6);
}
</style>
